import React, { useCallback, useMemo, useRef, useState } from 'react';
import { EXPERIMENTS } from '../../../../constants/experiments';
import { useConfig } from '../../../../contexts/config-context';
import { useExperiments } from '../../../../contexts/experiments-context';
import { useId } from '../../../../hooks/use-id';
import { useTranslation } from '../../../../sdk/components';
import { CoreDropdownComponent } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdown/CoreDropdown';
import { OptionFactory } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownOption';
import { FloatingDropdown } from '../../../autocomplete/wut/WixUiCoreComponents/components/FloatingDropdown';
import { toLongCountryCode } from '../phone-input-utils';
import { CountryOption } from './country-option';
import { classes, st } from './dropdown-with-search.st.css';
import { FlagButton } from './flag-button';
import { Search } from './search';
export const DropdownWithSearch = ({ className, theme, countryCode, countries, onSelect, searchPhrase, onSearch, onKeyDown, readOnly, disabled, ariaLabel })=>{
    const { isRTL: rtl } = useConfig();
    const { isExperimentEnabled } = useExperiments();
    const useFloatingDropdown = isExperimentEnabled(EXPERIMENTS.FLOATING_PHONE_COUNTRY_DROPDOWN);
    const longCountryCode = toLongCountryCode(countryCode);
    const flagButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchStyle, setSearchStyle] = useState({});
    const { comboboxProps, listboxProps, setActiveDescendant } = useCombobox();
    const options = useMemo(()=>countries.map(createOption), [
        countries
    ]);
    const initialSelectedIds = useMemo(()=>countryCode ? [
            countryCode
        ] : [], [
        countryCode
    ]);
    const firstCountryCode = countries[0]?.shortCountryCode;
    const focusOnOption = searchPhrase ? firstCountryCode : countryCode ?? firstCountryCode;
    const handleKeydown = useCallback((event)=>{
        switch(event.key){
            case 'Escape':
                event.preventDefault();
                dropdownRef.current?.close();
                flagButtonRef.current?.focus();
                break;
            case 'ArrowUp':
            case 'ArrowDown':
            case 'Enter':
            case 'Tab':
                dropdownRef.current?.onKeyDown(event);
                break;
        }
        onKeyDown(event);
    }, [
        onKeyDown
    ]);
    // The dropdown content (search input + options) is rendered in a portal
    // outside this field. When it closes, the focused node is unmounted and the
    // browser drops focus to <body>, so the next Tab restarts from the top of the
    // page. Return focus to the trigger to keep the tab order intact. Guarded so
    // we never steal focus when the user closed the popover by clicking another
    // focusable element (in that case activeElement is that element, not <body>).
    const restoreFocusToTrigger = useCallback(()=>{
        requestAnimationFrame(()=>{
            if (document.activeElement === document.body) {
                flagButtonRef.current?.focus();
            }
        });
    }, []);
    const handleSelect = useCallback((option)=>{
        const _countryCode = option?.id;
        onSelect(_countryCode);
        setIsOpen(false);
    }, [
        onSelect
    ]);
    const handleSearch = useCallback((event)=>onSearch(event.target.value), [
        onSearch
    ]);
    const handleExpandChange = useCallback((open)=>{
        setIsOpen(open);
        onSearch('');
        if (!open) {
            restoreFocusToTrigger();
        }
    }, [
        onSearch,
        restoreFocusToTrigger
    ]);
    const handleSearchMounted = useCallback((input)=>{
        const width = input?.getBoundingClientRect()?.width;
        width && setSearchStyle({
            minWidth: width
        });
    }, []);
    const handleOptionHover = useCallback((option)=>{
        option && setActiveDescendant(option._DOMid);
    }, [
        setActiveDescendant
    ]);
    const flagButton = /*#__PURE__*/ React.createElement(FlagButton, {
        readOnly: readOnly,
        ref: flagButtonRef,
        arrowDirection: isOpen ? 'up' : 'down',
        longCountryCode: longCountryCode,
        ariaLabel: ariaLabel,
        "aria-expanded": useFloatingDropdown ? isOpen : undefined,
        "aria-haspopup": useFloatingDropdown ? 'listbox' : undefined
    });
    const fixedHeader = /*#__PURE__*/ React.createElement("div", {
        style: searchStyle,
        ref: handleSearchMounted,
        className: classes.searchContainer
    }, /*#__PURE__*/ React.createElement(Search, {
        ...comboboxProps,
        className: className,
        theme: theme,
        autoFocus: true,
        value: searchPhrase,
        onChange: handleSearch,
        onKeyDown: handleKeydown
    }));
    const sharedProps = {
        initialSelectedIds,
        options,
        placement: 'bottom-start',
        openTrigger: 'click',
        onSelect: handleSelect,
        onExpandedChange: handleExpandChange,
        onOptionHover: handleOptionHover,
        focusOnOption,
        noResultsText: t('field.phone.country-selector-dropdown.no-result'),
        contentId: listboxProps.id,
        contentRole: listboxProps.role,
        fixedHeader,
        readOnly,
        disabled
    };
    if (useFloatingDropdown) {
        return /*#__PURE__*/ React.createElement(FloatingDropdown, {
            ...sharedProps,
            dir: "ltr",
            ref: dropdownRef,
            className: st(classes.floatingDropdown, {
                rtl
            }),
            contentClassName: st(classes.floatingDropdownContainer, {
                theme
            }),
            dropdownContentClassName: classes.floatingDropdownContent
        }, flagButton);
    } else {
        return /*#__PURE__*/ React.createElement(CoreDropdownComponent, {
            ...sharedProps,
            ref: dropdownRef,
            appendTo: "window",
            className: st(classes.dropdown, {
                rtl
            }),
            contentClassName: st(classes.dropdownContainer, {
                rtl,
                theme
            }),
            eventsEnabled: false
        }, flagButton);
    }
};
function createOption({ longCountryCode, shortCountryCode, callingCode, countryName }) {
    return OptionFactory.create({
        id: shortCountryCode,
        render: ()=>/*#__PURE__*/ React.createElement(CountryOption, {
                callingCode: callingCode,
                longCountryCode: longCountryCode,
                countryName: countryName
            })
    });
}
function useCombobox() {
    const listboxId = useId();
    const [activeDescendant, setActiveDescendant] = useState();
    const comboboxProps = {
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-haspopup': 'listbox',
        'aria-expanded': true,
        'aria-owns': listboxId,
        'aria-activedescendant': activeDescendant
    };
    const listboxProps = {
        role: 'listbox',
        id: listboxId
    };
    return {
        comboboxProps,
        listboxProps,
        setActiveDescendant
    };
}

//# sourceMappingURL=dropdown-with-search.js.map